import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import OmsService from "src/_cores/_services/oms.service";
import {
  OrderStatisticsResponse,
} from "src/_pages/admin/interfaces/oms-interface";
import { mapOrderStatisticToHeader } from "src/_shares/_helpers/order.helper";
import OrdersStatisticalView from "./OrdersStatisticalView";
import { StatCardProps } from "../star-card";
import { Autocomplete, Checkbox, Grid, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import UserService from "src/_cores/_services/user.service";
import { IUserGetParam } from "src/_pages/admin/interfaces/user-interface";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const OrdersStatistical = () => {
  const [data, setData] = useState<OrderStatisticsResponse | null>(null);
  const [filterUsers, setFilterUsers] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [orders, setOrders] = useState<number[]>([]);
  const [headerData, setHeaderData] = useState<StatCardProps[]>([]);

  const today = dayjs();
  const thirtyDaysAgo = today.subtract(30, "day");

  const [params, setParams] = useState<{
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
    status: string | null;
    userNames: string[];
  }>({
    fromDate: thirtyDaysAgo,
    toDate: today,
    status: null,
    userNames: [],
  });

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    fetchStatistic();
  }, [params.fromDate, params.toDate, params.userNames]);

  async function getUsers() {
    const params = {
      pageIndex: 1,
      pageSize: 1000,
    } as IUserGetParam;
    const res = (await UserService.getUserList(params)) as any;
    if (res?.isSucceed) {
      setUsers(res.data.items);
    }
  }

    const onChangeDate = (type: "fromDate" | "toDate", date: Dayjs | null) => {
      setParams((prev) => ({
        ...prev,
        [type]: date ? date.startOf("day") : null,
      }));
    };

  const onChangeFilterUsers = (items: any[]) => {
    const _userNames = items.map((_) => _.userName) as any;
    setFilterUsers(_userNames);
    const _params = { ...params, userNames: _userNames || [] };
    setParams(_params);
  };

  const fetchStatistic = async () => {
    const from = params.fromDate
      ? dayjs(params.fromDate).format("YYYY-MM-DD")
      : null;

    const to = params.toDate
      ? dayjs(params.toDate).format("YYYY-MM-DD")
      : null;

    const res = (await OmsService.getOrderStatistic({
      fromDate: from,
      toDate: to,
      status: null,
      userNames: params.userNames,
    })) as any;

    const data = res?.data;

    if (data) {
      setData(data);

      const labels = data.byMonth?.map((x: any) => x?.label ?? "") ?? [];
      const orders = data.byMonth?.map((x: any) => x?.orderCount ?? 0) ?? [];

      setLabels(labels);
      setOrders(orders);

      const mapped = mapOrderStatisticToHeader(data);
      setHeaderData(mapped);
    }
  };

  if (!data) return null;

  return (
    <>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Từ ngày"
              value={params.fromDate ? dayjs(params.fromDate) : null}
              onChange={(newValue) => onChangeDate("fromDate", newValue)}
              format="DD/MM/YYYY"
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Đến ngày"
              value={params.toDate ? dayjs(params.toDate) : null}
              onChange={(newValue) => onChangeDate("toDate", newValue)}
              format="DD/MM/YYYY"
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Autocomplete
            multiple
            id="checkboxes-tags-demo"
            options={users}
            disableCloseOnSelect
            getOptionLabel={(item: any) =>
              `${item.userId} - ${item.userFullName}`
            }
            value={
              users.length > 0 && filterUsers
                ? users.filter((item: any) =>
                    filterUsers.some((_: any) => _ === item.userName),
                  )
                : []
            }
            renderOption={(props, item: any, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {item.userId} - {item.userFullName}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Người dùng"
                placeholder="Chọn người dùng"
                size="small"
              />
            )}
            onChange={(event, items: any) => onChangeFilterUsers(items)}
            size="small"
          />
        </Grid>
      </Grid>
      <OrdersStatisticalView
        labels={labels}
        orders={orders}
        headerData={headerData}
      />
    </>
  );
};

export default OrdersStatistical;
